import {
  ListPage,
  RefreshButton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusBadge,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@skylabs-digital/backoffice-kit';
import { useSnackbar } from '@skylabs-digital/react-proto-kit';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { type DeliveriesPage, useWebhookActions } from '../api/webhooks.api';
import { AppFilter } from '../components/shared/AppFilter';
import { Pager } from '../components/shared/Pager';
import { DeliveriesTable } from '../components/webhooks/DeliveriesTable';
import { EndpointsTable } from '../components/webhooks/EndpointsTable';
import { WebhookChart } from '../components/webhooks/WebhookChart';
import { WebhookKpis } from '../components/webhooks/WebhookKpis';
import { EVENT_TYPES } from '../schemas/activity.schema';
import { type ProcessingWindow, processingWindowSchema } from '../schemas/processing.schema';
import type { WebhookDelivery, WebhooksOverview, WebhooksSeries } from '../schemas/webhook.schema';

const LIMIT = 25;
const ANY = '__any__';
const WINDOWS: ProcessingWindow[] = ['1h', '6h', '24h', '7d'];
type Param = 'app' | 'window' | 'endpoint' | 'status' | 'event';

export function WebhooksPage() {
  const api = useWebhookActions();
  const { showSnackbar } = useSnackbar();
  const [params, setParams] = useSearchParams();
  const appId = params.get('app') ?? undefined;
  const endpointId = params.get('endpoint') ?? undefined;
  const status =
    params.get('status') === 'FAILED' || params.get('status') === 'DELIVERED'
      ? (params.get('status') as 'FAILED' | 'DELIVERED')
      : undefined;
  const eventType = params.get('event') ?? undefined;
  const parsed = processingWindowSchema.safeParse(params.get('window'));
  const window: ProcessingWindow = parsed.success ? parsed.data : '24h';
  const [page, setPage] = useState(1);
  const [overview, setOverview] = useState<WebhooksOverview | null>(null);
  const [series, setSeries] = useState<WebhooksSeries | null>(null);
  const [deliveries, setDeliveries] = useState<DeliveriesPage | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const setParam = (key: Param, value: string | undefined) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    // Cambiar de app invalida el endpoint elegido.
    if (key === 'app') next.delete('endpoint');
    setParams(next, { replace: true });
    setPage(1);
  };

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const [o, s, d] = await Promise.all([
        api.overview({ window, appId }),
        api.series({ window, appId }),
        api.deliveries({ appId, endpointId, status, eventType, page, limit: LIMIT }),
      ]);
      setOverview(o);
      setSeries(s);
      setDeliveries(d);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e : new Error('Failed to load webhook stats'));
    } finally {
      setRefreshing(false);
    }
  }, [api, window, appId, endpointId, status, eventType, page]);

  useEffect(() => {
    void load();
  }, [load]);

  const redeliver = useCallback(
    async (d: WebhookDelivery) => {
      if (!d.appId) return;
      try {
        await api.redeliver(d.appId, d.id);
        showSnackbar({
          message: `Queued ${d.eventId} again for ${d.endpointName ?? 'webhook'}`,
          variant: 'success',
        });
        await load();
      } catch {
        showSnackbar({ message: 'Could not redeliver this event', variant: 'error' });
      }
    },
    [api, showSnackbar, load],
  );

  const selectedEndpoint = overview?.endpoints.find((e) => e.endpointId === endpointId);

  return (
    <ListPage
      title="Webhooks"
      description="Every delivery Fisto made to the products’ endpoints: what failed, which endpoint is unhealthy and a way to send it again."
      actions={
        <div className="flex flex-wrap items-center gap-3">
          <AppFilter value={appId} onChange={(v) => setParam('app', v)} />
          <Tabs value={window} onValueChange={(v) => setParam('window', v)}>
            <TabsList>
              {WINDOWS.map((w) => (
                <TabsTrigger key={w} value={w}>
                  {w}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <RefreshButton onRefresh={load} isRefreshing={refreshing} label={null} />
        </div>
      }
    >
      <div className="space-y-6">
        {error ? <p className="text-sm text-destructive">{error.message}</p> : null}
        <WebhookKpis overview={overview} />
        <WebhookChart series={series} />
        <section className="space-y-2">
          <h2 className="text-base font-semibold">Endpoints</h2>
          <EndpointsTable
            rows={overview?.endpoints ?? []}
            loading={overview === null}
            onSelectEndpoint={(e) => {
              const next = new URLSearchParams(params);
              next.set('app', e.appId);
              next.set('endpoint', e.endpointId);
              setParams(next, { replace: true });
              setPage(1);
            }}
          />
        </section>
        <section className="space-y-2">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-base font-semibold">Deliveries</h2>
            {selectedEndpoint ? (
              <StatusBadge tone="info" dot={false}>
                {selectedEndpoint.name}
                <button
                  type="button"
                  className="ml-1"
                  aria-label="Clear endpoint filter"
                  onClick={() => setParam('endpoint', undefined)}
                >
                  ×
                </button>
              </StatusBadge>
            ) : null}
            <Select
              value={status ?? ANY}
              onValueChange={(v) => setParam('status', v === ANY ? undefined : v)}
            >
              <SelectTrigger className="w-36" aria-label="Status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Any status</SelectItem>
                <SelectItem value="DELIVERED">Delivered</SelectItem>
                <SelectItem value="FAILED">Failed</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={eventType ?? ANY}
              onValueChange={(v) => setParam('event', v === ANY ? undefined : v)}
            >
              <SelectTrigger className="w-48" aria-label="Event type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Any event</SelectItem>
                {EVENT_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">
              {deliveries ? `${deliveries.meta.total} deliveries` : ''}
            </span>
          </div>
          <DeliveriesTable
            rows={deliveries?.data ?? []}
            loading={deliveries === null}
            error={error}
            onRedeliver={redeliver}
          />
          <Pager meta={deliveries?.meta} page={page} onPage={setPage} />
        </section>
      </div>
    </ListPage>
  );
}
